import { clsx } from 'clsx'
import { useState } from 'react'

interface TextAreaProps {
  /**
   * Field id
   */
  id?: string
  /**
   * Field name
   */
  name?: string
  /**
   * Form label
   */
  label?: string
  /**
   * Is required
   */
  required?: boolean
  /**
   * Placeholder text
   */
  placeholder?: string
  /**
   * Value
   */
  value?: string
  /**
   * Error text
   */
  error?: string
  /**
   * Aria label
   */
  ariaLabel?: string
  /**
   * Form name
   */
  form?: string
  /**
   * Optional blur handler
   */
  onChange?: (e: string) => void
  /**
   * Blur handler
   */
  onBlur?: () => void
}

/**
 * Primary UI component for user interaction
 */
const TextArea = ({
  id,
  name,
  label,
  required = false,
  placeholder,
  value,
  error,
  ariaLabel,
  form,
  onChange,
  onBlur,
}: TextAreaProps) => {
  const kebabCase = (string: string) =>
    string
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase()

  return (
    <div className="relative flex flex-col gap-2">
      <label className="font-medium">
        {label}
        {required && <span className="text-red-600">*</span>}
      </label>
      <textarea
        className={clsx(
          'block w-full border bg-beige-200 p-3 placeholder:text-teal-500 focus:outline-none',
          {
            'border-red-600': error,
            'border-slate-400': !error,
          }
        )}
        name={name}
        id={name && kebabCase(name)}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        form={form}
        required={required}
        onChange={e => (onChange ? onChange(e.target.value) : undefined)}
        onBlur={() => (onBlur ? onBlur() : undefined)}
      />
      {error && <p className="mb-0 pt-2 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default TextArea
